import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";

// Utility: checks if menu or any child has a matching path
const isMenuActive = (menu, pathname) => {
  if (menu.path && pathname === menu.path) return true;
  if (menu.submenu) return menu.submenu.some((sub) => isMenuActive(sub, pathname));
  if (menu.children) return menu.children.some((child) => isMenuActive(child, pathname));
  return false;
};

const renderNestedMenu = ({
  items,
  isMobile,
  openSublist,
  toggleSublist,
  depth = 0,
  pathname
}) => {
  return (
    <ul className={`${isMobile ? "ml-4 space-y-1" : "absolute left-full top-0 mt-0 bg-white dark:bg-gray-800 shadow-lg rounded-md z-50"} ${depth > 0 && !isMobile ? "ml-2" : ""}`}>
      {items.map((item) => {
        const isActive = isMenuActive(item, pathname);
        return (
          <li key={item.title} className="relative">
            {item.children ? (
              <>
                <button
                  onClick={() => toggleSublist(item.title)}
                  className={`block w-full text-left px-4 py-2 rounded ${
                    isMobile ? "flex justify-between items-center" : ""
                  } hover:bg-gray-200 dark:hover:bg-gray-700 ${
                    isActive ? "bg-gray-300 text-black" : "text-white"
                  }`}
                >
                  <span>{item.title}</span>
                  <FontAwesomeIcon
                    icon={isMobile ? (openSublist === item.title ? faChevronDown : faChevronRight) : faChevronRight}
                    className="ml-2"
                  />
                </button>
                {openSublist === item.title &&
                  renderNestedMenu({
                    items: item.children,
                    isMobile,
                    openSublist,
                    toggleSublist,
                    depth: depth + 1,
                    pathname
                  })}
              </>
            ) : (
              <Link
                to={item.path}
                className={`block px-4 py-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded ${
                  isActive ? "bg-gray-300 text-black" : "text-white"
                }`}
              >
                {item.title}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const Menu = ({
  menuData,
  openDropdown,
  openSublist,
  toggleDropdown,
  toggleSublist,
  isMobile = false,
  handleMenuHover = () => {},
}) => {
  const { pathname } = useLocation();

  return (
    <ul className={isMobile ? "space-y-2" : "flex space-x-4"}>
      {menuData?.map((menu, index) => {
        const isActive = isMenuActive(menu, pathname);

        return (
          <li
            key={menu.title}
            className="relative"
            onMouseEnter={() => !isMobile && handleMenuHover(index)}
            onMouseLeave={() => !isMobile && openDropdown === index && handleMenuHover(null)}
          >
            {menu.path ? (
              <Link
                to={menu.path}
                className={`block px-4 py-2 rounded ${
                  isMobile ? "text-left w-full" : ""
                } hover:bg-gray-200 hover:text-black ${
                  isActive ? "bg-gray-300 text-black" : "text-white"
                }`}
              >
                {menu.title}
              </Link>
            ) : (
              <button
                onClick={() => toggleDropdown(index)}
                className={`block w-full text-left px-4 py-2 rounded ${
                  isMobile ? "flex justify-between items-center" : ""
                } hover:bg-gray-200 hover:text-black ${
                  isActive ? "bg-gray-300 text-black" : "text-white"
                }`}
              >
                <span>{menu.title}</span>
                {menu.submenu && (
                  <FontAwesomeIcon
                    icon={
                      isMobile
                        ? openDropdown === index
                          ? faChevronDown
                          : faChevronRight
                        : faChevronDown
                    }
                    className={`ml-2 transition-transform ${
                      openDropdown === index && !isMobile ? "rotate-180" : ""
                    }`}
                  />
                )}
              </button>
            )}

            {menu.submenu && (isMobile ? openDropdown === index : openDropdown === index) && (
              <ul
                className={`${
                  isMobile
                    ? "ml-4 mt-1 space-y-1"
                    : "absolute left-0 mt-0 w-56 bg-white shadow-lg rounded-md z-50"
                }`}
              >
                {menu.submenu.map((submenu) => {
                  const isSubActive = isMenuActive(submenu, pathname);

                  return (
                    <li key={submenu.title} className="relative">
                      {submenu.children ? (
                        <>
                          <button
                            onClick={() => toggleSublist(submenu.title)}
                            className={`block w-full text-left px-4 py-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded ${
                              isMobile ? "flex justify-between items-center" : ""
                            } ${isSubActive ? "bg-gray-300 text-black" : "text-white"}`}
                          >
                            <span>{submenu.title}</span>
                            <FontAwesomeIcon
                              icon={
                                isMobile
                                  ? openSublist === submenu.title
                                    ? faChevronDown
                                    : faChevronRight
                                  : faChevronRight
                              }
                              className="ml-2"
                            />
                          </button>
                          {openSublist === submenu.title &&
                            renderNestedMenu({
                              items: submenu.children,
                              isMobile,
                              openSublist,
                              toggleSublist,
                              pathname
                            })}
                        </>
                      ) : (
                        <Link
                          to={submenu.path}
                          className={`block px-4 hover:bg-gray-200 ${isMobile ? "text-white py-1":"py-2"} dark:hover:bg-gray-700 rounded ${
                            isSubActive ? "bg-gray-300 text-black" : "text-black"
                          } ${isMobile && isSubActive && "text-black"}`}
                        >
                          {submenu.title}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default Menu;
